import React, { createContext, useContext, useState, useEffect } from "react";
import apiClient, { formatApiErrorDetail } from "@/utils/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null); // null: loading/checking, false: logged out, obj: logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      if (data && data.authenticated) {
        setAdminUser(data.user);
      } else {
        setAdminUser(false);
      }
    } catch (e) {
      setAdminUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const loginWithPassword = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      if (data && data.token) {
        localStorage.setItem("afl_admin_token", data.token);
        setAdminUser(data.user);
        return { success: true };
      }
      return { success: false, error: "Authentication failed." };
    } catch (err) {
      return {
        success: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || "Invalid email or password",
      };
    }
  };

  const loginWithPin = async (pin) => {
    try {
      const { data } = await apiClient.post("/auth/pin-login", { pin });
      if (data && data.token) {
        localStorage.setItem("afl_admin_token", data.token);
        setAdminUser(data.user);
        return { success: true };
      }
      return { success: false, error: "Invalid PIN." };
    } catch (err) {
      return {
        success: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || "Invalid Admin PIN",
      };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem("afl_admin_token");
      setAdminUser(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        adminUser,
        isAuthenticated: !!adminUser,
        loading,
        loginWithPassword,
        loginWithPin,
        logout,
        refreshAdmin: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
